import { useState, useEffect, useRef, useCallback } from "react";
import Layout from "../components/Layout";
import { motion } from "framer-motion";
import apiClient from "../api/client";
import moment from "moment-timezone";
import ExportModal from "../components/ExportModal";
import { saveAs } from "file-saver";

export default function ServerLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState({
    eventType: "",
    status: "",
    startDate: "",
    endDate: "",
  });
  const [availableFilters, setAvailableFilters] = useState({
    eventTypes: [],
    statuses: [],
  });
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const observer = useRef();
  const lastLogElementRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, loadingMore, hasMore]
  );

  const fetchLogs = async (pageNum = 1, isLoadingMore = false) => {
    try {
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const queryParams = new URLSearchParams({
        page: pageNum,
        limit: 50,
        ...(filters.eventType && { eventType: filters.eventType }),
        ...(filters.status && { status: filters.status }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
      });

      const response = await apiClient.get(`api/server-logs?${queryParams}`);

      if (response.ok && response.data) {
        if (isLoadingMore) {
          setLogs((prevLogs) => [...prevLogs, ...response.data.logs]);
        } else {
          setLogs(response.data.logs);
          setAvailableFilters({
            eventTypes: response.data.filters.eventTypes,
            statuses: response.data.filters.statuses,
          });
        }
        setHasMore(response.data.pagination.hasMore);
      }
    } catch (error) {
      console.error("Error fetching server logs:", error);
      setError("Errore nel caricamento dei log del server");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setLogs([]);
    fetchLogs(1, false);
  }, [filters]);

  useEffect(() => {
    if (page > 1) {
      fetchLogs(page, true);
    }
  }, [page]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "error":
        return "bg-red-100 text-red-800";
      case "success":
        return "bg-green-100 text-green-800";
      case "started":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleExport = async (dateRange) => {
    try {
      const queryParams = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...(filters.eventType && { eventType: filters.eventType }),
        ...(filters.status && { status: filters.status }),
      });

      const response = await apiClient.get(
        `api/server-logs/export?${queryParams}`,
        {},
        { responseType: "blob" }
      );

      if (response.ok) {
        const blob = new Blob([response.data], {
          type: "text/csv;charset=utf-8",
        });
        saveAs(
          blob,
          `server_logs_${dateRange.startDate}_${dateRange.endDate}.csv`
        );
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      setError("Errore durante l'esportazione dei dati");
    }
  };

  if (loading && !loadingMore) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="loading loading-spinner text-red-500"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-red-500 text-xl">{error}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen p-6"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl font-bold text-gray-800 mb-8"
            >
              Log del Server
            </motion.h1>
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="btn btn-primary btn-sm"
            >
              Esporta CSV
            </button>
          </div>

          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <select
              className="select select-bordered w-full"
              value={filters.eventType}
              onChange={(e) => handleFilterChange("eventType", e.target.value)}
            >
              <option value="">Tutti gli eventi</option>
              {availableFilters.eventTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <select
              className="select select-bordered w-full"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
            >
              <option value="">Tutti gli stati</option>
              {availableFilters.statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <input
              type="datetime-local"
              className="input input-bordered w-full"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              placeholder="Data inizio"
            />

            <input
              type="datetime-local"
              className="input input-bordered w-full"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              placeholder="Data fine"
            />
          </div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: {
                transition: {
                  staggerChildren: 0.05,
                },
              },
            }}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data/Ora
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo Evento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Messaggio
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dettagli
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log, index) => {
                    const isLastElement = index === logs.length - 1;
                    const rowProps = isLastElement
                      ? { ref: lastLogElementRef }
                      : {};

                    return (
                      <motion.tr
                        key={log.id}
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: { opacity: 1, y: 0 },
                        }}
                        {...rowProps}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {moment(log.timestamp)
                            .tz("Europe/Rome")
                            .format("DD/MM/YYYY HH:mm:ss")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {log.event_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                              log.status
                            )}`}
                          >
                            {log.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {log.message}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {log.details && (
                            <pre className="text-xs">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>

          {loadingMore && (
            <div className="flex justify-center my-4">
              <div className="loading loading-spinner text-red-500"></div>
            </div>
          )}
        </div>
      </motion.div>
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title="Esporta log del server"
      />
    </Layout>
  );
}
